// /admin/usage — the return leg of a credit top-up checkout (ws crm,
// 2026-10-01, Nika's Stripe Sandbox run: he paid $20, the page came back to
// /admin/usage with NO message and NO credits).
//
// Stripe sends the person back to the usage page. A paid checkout carries
// `session_id` (the server writes it into the success URL); a cancelled one
// carries the page's own `topup=cancelled` marker (`topupCheckoutUrls`).
// On that return the page:
//   • confirms the payment through the SDK (`confirmCreditTopup(workspaceId,
//     sessionId)`, the `POST /core/workspaces/:workspaceId/billing/topups/
//     confirm` route → { applied, alreadyApplied, credits, balance }),
//     shows ONE line from the server's answer ("Payment received. 1,000
//     credits added.") or the failure with a retry, refreshes the balance,
//     and takes the return params off the address bar through the router;
//   • for a cancelled checkout shows a calm "No payment was made." line.
// An SDK without the confirm method keeps today's behaviour (no line, the
// URL untouched) — the page never breaks on an older SDK.
//
// State: ONE root key, `_topupReturn` (state.js, WORKSPACE_SCOPED):
//   null | { status: 'confirming' | 'paid' | 'failed' | 'cancelled',
//            sessionId?, credits?, error? }
// `this` is an element on the page in every export below (el.call).

// The return params of a URL string: { sessionId, cancelled } or null.
export const topupReturnParse = function topupReturnParse(href) {
  let url
  try {
    url = new URL(String(href || ''))
  } catch (_) {
    return null
  }
  const sessionId = url.searchParams.get('session_id') || null
  const cancelled = url.searchParams.get('topup') === 'cancelled'
  if (!sessionId && !cancelled) return null
  return { sessionId, cancelled: cancelled && !sessionId }
}

// The success / cancel URLs for a new checkout: the USAGE PAGE of the same
// workspace (it is the page that reads the return — topupReturnHandle), with
// none of the current address's query; the cancel leg adds `topup=cancelled`.
// The server adds `session_id` to the success leg. Built from the workspace
// prefix, never from the address as it stands: on dev 2026-10-01 a paid
// $1,000 top-up came back to `/w/unreal/default?surface=appstore` (the
// address the shell showed while the usage page was open), where nothing read
// `session_id`, and the payment was never confirmed.
export const topupCheckoutUrls = function topupCheckoutUrls(href) {
  const el = this
  let url
  try {
    url = new URL(String(href || ''))
  } catch (_) {
    return { successUrl: href || '', cancelUrl: href || '' }
  }
  const route =
    el && typeof el.call === 'function' ? el.call('stripWorkspacePrefix', url.pathname) : null
  if (typeof route === 'string') {
    const prefix =
      route === '/'
        ? url.pathname.replace(/\/+$/, '')
        : url.pathname.slice(0, url.pathname.length - route.length)
    url.pathname = `${prefix}/admin/usage`
    url.search = ''
    url.hash = ''
  }
  url.searchParams.delete('session_id')
  url.searchParams.delete('topup')
  const successUrl = url.toString()
  url.searchParams.set('topup', 'cancelled')
  return { successUrl, cancelUrl: url.toString() }
}

// Take the return params off the address bar through the router (a REPLACE,
// so Back never re-runs the confirm). The router path is app-relative; the
// workspace router puts the /w/<org>/<ws> prefix back.
export const topupReturnClearUrl = function topupReturnClearUrl() {
  const el = this
  const doc = el && el.context && el.context.document
  const href = (doc && doc.URL) || (globalThis.document && globalThis.document.URL) || ''
  let url
  try {
    url = new URL(href)
  } catch (_) {
    return false
  }
  if (!url.searchParams.has('session_id') && !url.searchParams.has('topup')) return false
  url.searchParams.delete('session_id')
  url.searchParams.delete('topup')
  const bare = el.call('stripWorkspacePrefix', url.pathname) || url.pathname
  const root = typeof el.getRoot === 'function' ? el.getRoot() : null
  if (!root || typeof el.router !== 'function') return false
  el.router(bare + url.search + url.hash, root, {}, { replace: true, scrollToTop: false })
  return true
}

// The workspace the usage page is showing (pages/admin/usage.js
// `_usageWorkspaceId` — the same precedence).
const workspaceOf = (root) =>
  root?.workspace?.id || root?.workspace?._id || root?.jwtClaims?.active_workspace_id || null

// Confirm one session; the answer's credits drive the line.
export const topupReturnConfirm = async function topupReturnConfirm(root, sessionId) {
  const el = this
  if (!root || !sessionId) return null
  const sdk = el.call('getSDK')
  if (!sdk || typeof sdk.confirmCreditTopup !== 'function') return null
  const workspaceId = workspaceOf(root)
  if (!workspaceId) return null
  if (root._topupReturn && root._topupReturn.status === 'confirming') return null
  root.update({ _topupReturn: { status: 'confirming', sessionId } })
  try {
    const res = await sdk.confirmCreditTopup(workspaceId, sessionId)
    const data = (res && res.data) || res || {}
    if (workspaceOf(root) !== workspaceId) return null
    const credits = Number(data.credits ?? data.creditsAdded)
    root.update({
      _topupReturn: {
        status: 'paid',
        sessionId,
        credits: Number.isFinite(credits) ? credits : null
      }
    })
    // the balance row: a fresh read in the loader's own shape; the
    // confirm answer's balance when the read is not there
    let balance = data.balance ? { data: data.balance } : null
    if (typeof sdk.getCreditBalance === 'function') {
      try {
        balance = await sdk.getCreditBalance(workspaceId)
      } catch (_) {
        // keep the confirm answer's balance (or the row's last value)
      }
    }
    if (balance && workspaceOf(root) === workspaceId) root.update({ _usageBalance: balance })
    el.call('topupReturnClearUrl')
    return 'paid'
  } catch (err) {
    if (workspaceOf(root) !== workspaceId) return null
    root.update({
      _topupReturn: {
        status: 'failed',
        sessionId,
        error: (err && err.cause && err.cause.error) || (err && err.message) || null
      }
    })
    return 'failed'
  }
}

// The page's one entry: read the return params and act on them once.
export const topupReturnHandle = async function topupReturnHandle(root) {
  const el = this
  if (!root) return null
  const doc = el && el.context && el.context.document
  const params = el.call(
    'topupReturnParse',
    (doc && doc.URL) || (globalThis.document && globalThis.document.URL) || ''
  )
  if (!params) return null
  if (params.cancelled) {
    root.update({ _topupReturn: { status: 'cancelled' } })
    el.call('topupReturnClearUrl')
    return 'cancelled'
  }
  const sdk = el.call('getSDK')
  // An SDK without the confirm method: today's behaviour, nothing else.
  if (!sdk || typeof sdk.confirmCreditTopup !== 'function') return null
  if (!workspaceOf(root)) {
    // a cold deep link: the workspace record lands after the page mounts
    el.call('onActiveWorkspaceFull', () => el.call('topupReturnConfirm', root, params.sessionId))
    return 'waiting'
  }
  return el.call('topupReturnConfirm', root, params.sessionId)
}

// The one line the page shows for a return, in the session language.
export const topupReturnText = function topupReturnText(state) {
  const el = this
  const r = state && state._topupReturn
  if (!r) return ''
  if (r.status === 'paid') {
    if (r.credits == null) return el.call('polyglot', 'admin.usage.topup.returnPaidNoCount')
    const n = Number(r.credits).toLocaleString('en-US', { maximumFractionDigits: 2 })
    return el.call('polyglot', 'admin.usage.topup.returnPaid').replace('{credits}', n)
  }
  if (r.status === 'confirming') return el.call('polyglot', 'admin.usage.topup.returnConfirming')
  if (r.status === 'failed') return el.call('polyglot', 'admin.usage.topup.returnFailed')
  if (r.status === 'cancelled') return el.call('polyglot', 'admin.usage.topup.returnCancelled')
  return ''
}
